import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Button } from '@/components/ui/button';
import { useAccount } from '@/api/useAccount';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { useCustomerSearch } from './customers/useCustomers';
import { useJobListFilters, toApiFilters } from './jobs/useJobListFilters';
import { useJobsList, useTechnicians, type Job } from './jobs/useJobs';

const JOB_STATUSES = [
  'Unscheduled',
  'Scheduled',
  'EnRoute',
  'InProgress',
  'Complete',
  'Invoiced',
  'Closed',
  'Canceled',
] as const;

interface SortableColumn {
  key: string;
  labelKey: string;
  /**
   * Dropped from the table below `md` (M1-22). Six columns need 659px and a
   * phone gives the table 342px, so a technician had to scroll a table
   * sideways to read one job. The values are not lost — `JobRow` restates
   * them stacked under the customer name, in the cell that stays.
   */
  compactHidden?: boolean;
}

const COLUMNS: SortableColumn[] = [
  { key: 'customerName', labelKey: 'jobs.columnCustomer' },
  { key: 'jobType', labelKey: 'jobs.columnType', compactHidden: true },
  { key: 'status', labelKey: 'jobs.columnStatus' },
  { key: 'assignedUserName', labelKey: 'jobs.columnTechnician', compactHidden: true },
  { key: 'scheduledStart', labelKey: 'jobs.columnScheduled', compactHidden: true },
  { key: 'priority', labelKey: 'jobs.columnPriority', compactHidden: true },
];

/**
 * Hides a cell below `md` while leaving it a real table cell above it.
 * `display: none` takes the column out of the accessibility tree entirely, so
 * exactly one of the two presentations — the wide table or `JobRow`'s stacked
 * summary — is ever exposed, never both.
 */
const COMPACT_HIDDEN = 'hidden md:table-cell';

/**
 * The job list, filtered (M1-15).
 *
 * Rows link to the detail page (M1-13). Only the customer name is the link,
 * matching Customers.tsx's own list — the row is not a giant click target,
 * and a screen reader user tabbing through the table meets one link per row,
 * not six identical ones.
 *
 * State lives in the URL, like every other filtered screen here (Customers,
 * Dispatch), with one addition: a completely bare visit is seeded from the
 * signed-in user's last saved filters, and changes made after that are saved
 * back — both live in `useJobListFilters`, not in this component.
 *
 * <b>A Technician reaches this screen too, as of M1-22</b>, and sees only the
 * jobs assigned to them — narrowed by the API, not by anything here, so the
 * page never has to be trusted to have applied a filter correctly. What IS
 * decided here is what they are shown: the technician and customer filters
 * need `users.read`/`customers.read`, which they do not hold, so those two
 * controls and their queries are dropped rather than left to 403 quietly.
 */
export function Jobs() {
  const { t } = useTranslation();
  const account = useAccount();
  const timeZone = account.data?.timezone;
  const hasPermission = useHasPermission();
  const canReadAllJobs = hasPermission(Permissions.readAllJobs);

  const { state: filters, setState } = useJobListFilters(timeZone);
  const technicians = useTechnicians(canReadAllJobs);
  const customers = useCustomerSearch('', canReadAllJobs);

  // The filter row is seven controls; on a phone it buried the list itself
  // under a screenful of them. Collapsed behind a disclosure below `md`, and
  // unconditionally visible above it — the `md:flex` below wins whatever this
  // holds, so a dispatcher never sees the toggle at all.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const apiFilters = toApiFilters(filters, timeZone);
  const hasDateFilter = filters.fromDate !== null || filters.toDate !== null;
  const jobs = useJobsList(apiFilters, !hasDateFilter || timeZone !== undefined);

  // The generated client types int32 fields as `number | string` (a quirk of
  // how the API's OpenAPI doc describes them) even though the API always
  // sends a JSON number — normalized once here rather than coerced at every
  // arithmetic use below.
  const page = jobs.data
    ? {
        items: jobs.data.items,
        totalCount: Number(jobs.data.totalCount),
        page: Number(jobs.data.page),
        pageSize: Number(jobs.data.pageSize),
      }
    : undefined;
  const totalPages = page ? Math.max(1, Math.ceil(page.totalCount / page.pageSize)) : 1;

  function toggleSort(column: string) {
    setState(
      {
        sortBy: column,
        sortDirection: filters.sortBy === column && filters.sortDirection === 'asc' ? 'desc' : 'asc',
      },
      { resetPage: true },
    );
  }

  /** Only meaningful for the column currently sorted on — see the `aria-sort` note above. */
  function ariaSortFor(column: string): 'ascending' | 'descending' {
    return filters.sortBy === column && filters.sortDirection === 'desc' ? 'descending' : 'ascending';
  }

  function technicianSelectValue(): string {
    if (filters.unassignedOnly) {
      return 'unassigned';
    }
    return filters.assignedUserId ?? '';
  }

  function onTechnicianChange(value: string) {
    if (value === '') {
      setState({ assignedUserId: null, unassignedOnly: false });
    } else if (value === 'unassigned') {
      setState({ assignedUserId: null, unassignedOnly: true });
    } else {
      setState({ assignedUserId: value, unassignedOnly: false });
    }
  }

  function clearFilters() {
    setState({
      status: null,
      assignedUserId: null,
      unassignedOnly: false,
      jobType: '',
      fromDate: null,
      toDate: null,
      includeUnscheduled: true,
      customerId: null,
    });
  }

  const hasAnyFilter =
    filters.status !== null ||
    filters.assignedUserId !== null ||
    filters.unassignedOnly ||
    filters.jobType.trim() !== '' ||
    filters.fromDate !== null ||
    filters.toDate !== null ||
    !filters.includeUnscheduled ||
    filters.customerId !== null;

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('nav.jobs')}</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t(canReadAllJobs ? 'jobs.subtitle' : 'jobs.subtitleMine')}
        </p>
      </div>

      <button
        type="button"
        className="w-full rounded-md border px-3 py-2 text-sm hover:bg-[var(--color-muted)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none md:hidden"
        aria-expanded={filtersOpen}
        aria-controls="job-filters"
        onClick={() => setFiltersOpen((open) => !open)}
      >
        {t(filtersOpen ? 'jobs.filters.hide' : 'jobs.filters.show')}
        {hasAnyFilter ? ` (${t('jobs.filters.activeTag')})` : ''}
      </button>

      <div
        id="job-filters"
        className={cn('flex-wrap items-end gap-3 md:flex', filtersOpen ? 'flex' : 'hidden')}
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">{t('jobs.filters.status')}</span>
          <select
            className={fieldClass}
            value={filters.status ?? ''}
            onChange={(event) => setState({ status: event.target.value || null })}
          >
            <option value="">{t('jobs.filters.allStatuses')}</option>
            {JOB_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`common.jobStatus.${status}`, { defaultValue: status })}
              </option>
            ))}
          </select>
        </label>

        {canReadAllJobs ? (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">{t('jobs.filters.technician')}</span>
            <select
              className={fieldClass}
              value={technicianSelectValue()}
              onChange={(event) => onTechnicianChange(event.target.value)}
            >
              <option value="">{t('jobs.filters.allTechnicians')}</option>
              <option value="unassigned">{t('jobs.filters.unassignedOption')}</option>
              {(technicians.data ?? []).map((technician) => (
                <option key={technician.id} value={technician.id}>
                  {technician.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {canReadAllJobs ? (
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-[var(--color-muted-foreground)]">{t('jobs.filters.customer')}</span>
            <select
              className={fieldClass}
              value={filters.customerId ?? ''}
              onChange={(event) => setState({ customerId: event.target.value || null })}
            >
              <option value="">{t('jobs.filters.allCustomers')}</option>
              {(customers.data ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">{t('jobs.filters.jobType')}</span>
          <input
            type="text"
            className={fieldClass}
            value={filters.jobType}
            placeholder={t('jobs.filters.jobTypePlaceholder')}
            onChange={(event) => setState({ jobType: event.target.value })}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">{t('jobs.filters.from')}</span>
          <input
            type="date"
            className={fieldClass}
            value={filters.fromDate ?? ''}
            onChange={(event) => setState({ fromDate: event.target.value || null })}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">{t('jobs.filters.to')}</span>
          <input
            type="date"
            className={fieldClass}
            value={filters.toDate ?? ''}
            onChange={(event) => setState({ toDate: event.target.value || null })}
          />
        </label>

        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={filters.includeUnscheduled}
            onChange={(event) => setState({ includeUnscheduled: event.target.checked })}
          />
          {t('jobs.filters.includeUnscheduled')}
        </label>

        {/* Sorting is done by tapping a column header — and below `md` four of
            the six headers are gone with their columns, so it needs a control
            of its own there. Deliberately not shown on a wide screen, where
            the headers are both present and the more direct affordance. */}
        <label className="flex flex-col gap-1 text-sm md:hidden">
          <span className="text-[var(--color-muted-foreground)]">{t('jobs.sortBy')}</span>
          <select
            className={fieldClass}
            value={`${filters.sortBy}:${filters.sortDirection}`}
            onChange={(event) => {
              const [sortBy, sortDirection] = event.target.value.split(':');
              setState({ sortBy, sortDirection: sortDirection as 'asc' | 'desc' }, { resetPage: true });
            }}
          >
            {COLUMNS.flatMap((column) =>
              (['asc', 'desc'] as const).map((direction) => (
                <option key={`${column.key}:${direction}`} value={`${column.key}:${direction}`}>
                  {t(`jobs.sortOption.${direction}`, { column: t(column.labelKey) })}
                </option>
              )),
            )}
          </select>
        </label>

        {hasAnyFilter ? (
          <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
            {t('jobs.filters.clear')}
          </Button>
        ) : null}
      </div>

      {jobs.isPending ? (
        <LoadingState label={t('jobs.loading')} />
      ) : jobs.isError ? (
        <ErrorState
          title={t('jobs.loadErrorTitle')}
          description={t('jobs.loadErrorDescription')}
          onRetry={() => void jobs.refetch()}
        />
      ) : !page || page.items.length === 0 ? (
        <EmptyState
          title={hasAnyFilter ? t('jobs.noMatchesTitle') : t('jobs.emptyTitle')}
          description={hasAnyFilter ? t('jobs.noMatchesDescription') : t('jobs.emptyDescription')}
        />
      ) : (
        <>
          <p role="status" aria-live="polite" className="sr-only">
            {t('jobs.resultCount', { count: page.totalCount })}
          </p>

          <div
            role="region"
            aria-label={t('jobs.tableRegion')}
            className="overflow-x-auto rounded-lg border"
            // WCAG 2.1.1 (M1-42).
            //
            // ⚠️ Measured in Chrome and it did NOT overflow, at 320, 640, 700 or
            // 768: 286px of content in 286px at the 320px reflow width, 606 in 606
            // at 640, 666 in 666 at 700. The compact breakpoint hides enough
            // columns that the rest wrap into the room available.
            //
            // So this is insurance, not a fix for something reproduced — kept for
            // the reason spelled out on the Customers table, which measured the
            // same and sits beside a service-history table that looked just as
            // safe and overflowed by 33px at 320.
            //
            // ⚠️ The sort buttons in the header row WOULD scroll this sideways as
            // you tab through them if it ever overflowed. That is a property of
            // having sortable columns, not of the container being reachable, and
            // it would go away the moment a column stopped being sortable.
            //
            // Named, because a focusable region a screen reader cannot announce
            // is half a fix. `<div role="region">` rather than a `<section>` for
            // the lint reason recorded in eslint.config.js; the computed role is
            // the same. The table keeps its own <caption>: that describes the
            // contents, this names the thing you land on.
            tabIndex={0}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t('jobs.tableCaption')}</caption>
              <thead className="border-b bg-[var(--color-muted)]">
                <tr>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className={cn(
                        'px-4 py-2 text-left font-medium',
                        column.compactHidden && COMPACT_HIDDEN,
                      )}
                      // `aria-sort` is a th/columnheader attribute, not a button
                      // one — axe flags it on the button (aria-allowed-attr).
                      // Omitted entirely on an unsorted column rather than set
                      // to "none": that is what the ARIA authoring practices
                      // call for, and every column reading "not sorted" would
                      // bury which one, if any, actually is.
                      aria-sort={filters.sortBy === column.key ? ariaSortFor(column.key) : undefined}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key)}
                        className="flex items-center gap-1 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                      >
                        {t(column.labelKey)}
                        {filters.sortBy === column.key ? (
                          <span aria-hidden="true">{filters.sortDirection === 'asc' ? '▲' : '▼'}</span>
                        ) : null}
                        {filters.sortBy === column.key ? (
                          <span className="sr-only">
                            {t(filters.sortDirection === 'asc' ? 'jobs.sortAscending' : 'jobs.sortDescending')}
                          </span>
                        ) : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.items.map((job) => (
                  <JobRow
                    key={job.id}
                    job={job}
                    timeZone={timeZone}
                    showTechnician={canReadAllJobs}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <p className="text-[var(--color-muted-foreground)]">
              {t('jobs.resultCount', { count: page.totalCount })}
            </p>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page.page <= 1}
                onClick={() => setState({ page: page.page - 1 }, { resetPage: false })}
              >
                {t('jobs.pagination.previous')}
              </Button>
              <span aria-live="polite">{t('jobs.pagination.pageOf', { page: page.page, totalPages })}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page.page >= totalPages}
                onClick={() => setState({ page: page.page + 1 }, { resetPage: false })}
              >
                {t('jobs.pagination.next')}
              </Button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

const fieldClass =
  'h-9 rounded-md border border-[var(--color-input)] bg-transparent px-2 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

function JobRow({
  job,
  timeZone,
  showTechnician,
  t,
}: {
  job: Job;
  timeZone: string | undefined;
  /** False for a Technician, whose every row is assigned to themselves. */
  showTechnician: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  // One Intl call, one clock — see the CONVENTIONS note on splitting a date
  // and its time across two formatters that default to different zones.
  const scheduled = job.scheduledStart
    ? formatDate(job.scheduledStart, { dateStyle: 'medium', timeStyle: 'short', timeZone })
    : t('jobs.notScheduled');
  const jobType = job.jobType ?? t('jobs.noJobType');
  const priority = t(`common.jobPriority.${job.priority}`, { defaultValue: job.priority });

  return (
    <tr className="border-b last:border-b-0">
      <td className="px-4 py-2 align-top font-medium">
        {/* `tap-target`: this is the one action on the row, and a one-line
            customer name gave it a 19px-tall hit area on a phone. The row
            itself is deliberately NOT the target — one link per row is what a
            screen reader meets, same as Customers.tsx. */}
        <Link
          to={`/jobs/${job.id}`}
          className="tap-target underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
        >
          {job.customerName}
        </Link>
        {/* The four columns that leave below `md`, restated where the row still
            has room. `display: none` on the wide cells above and on this block
            below means exactly one of the two is ever in the accessibility
            tree — a screen reader never hears the schedule twice. */}
        <span
          className="mt-1 block text-xs font-normal text-[var(--color-muted-foreground)] md:hidden"
          data-testid="job-row-compact-summary"
        >
          <span className="block">{scheduled}</span>
          <span className="block">
            {[jobType, showTechnician ? (job.assignedUserName ?? t('jobs.unassigned')) : null, priority]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
      </td>
      <td className={cn('px-4 py-2 align-top text-[var(--color-muted-foreground)]', COMPACT_HIDDEN)}>
        {jobType}
      </td>
      <td className="px-4 py-2 align-top">
        {/* Same `tap-target` metric as the customer link beside it — not
            because a status is tappable, but because that class is the only
            thing that changes either cell's first line height, and it changes
            them together. Without it the link's 44px touch box centres its
            text while this stays at the top, and the two read as belonging to
            different rows. Under `pointer: fine` neither rule applies and they
            align for the ordinary reason. */}
        <span className="tap-target">
          {t(`common.jobStatus.${job.status}`, { defaultValue: job.status })}
        </span>
      </td>
      <td className={cn('px-4 py-2 align-top text-[var(--color-muted-foreground)]', COMPACT_HIDDEN)}>
        {job.assignedUserName ?? t('jobs.unassigned')}
      </td>
      <td className={cn('px-4 py-2 align-top text-[var(--color-muted-foreground)]', COMPACT_HIDDEN)}>
        {scheduled}
      </td>
      <td className={cn('px-4 py-2 align-top', COMPACT_HIDDEN)}>{priority}</td>
    </tr>
  );
}
